import * as React from 'react'

import { Box, Button, Section } from '@factorialco/deprecated-design-system'
import { Field, Form } from 'factorial-form'
import moment from 'moment'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import DayPicker from 'modules/core/components/Inputs/DayPicker'
import Select from 'modules/core/components/Inputs/Select'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import type EnpsForm from 'modules/enps/store/models/Form'

interface Props {
  form: EnpsForm
}

function DateFrequency({ form: enpsForm }: Props) {
  const scheduleSurvey = i18n.t('enps.form.date_frequency.schedule_survey')
  const startSurvey = i18n.t('enps.form.date_frequency.start_survey')
  const stopSurvey = i18n.t('enps.form.date_frequency.stop_survey')

  const { start_on, next_cycle } = enpsForm.attributes
  const today = moment().startOf('day')
  const diffDays = moment(start_on).diff(today, 'days')
  const scheduled = !!next_cycle && diffDays > 0 && enpsForm.hasSchedule
  const readOnly = enpsForm.isActive && !scheduled

  const [running, setRunning] = React.useState(false)
  const [textBtn, setTextBtn] = React.useState(
    enpsForm.isDraft || enpsForm.isFinished ? startSurvey : stopSurvey
  )

  const periodicityOptions = [
    {
      value: 'monthly',
      label: i18n.t('enps.form.periodicity.monthly'),
    },
    {
      value: 'bi_monthly',
      label: i18n.t('enps.form.periodicity.bi_monthly'),
    },
    {
      value: 'quarterly',
      label: i18n.t('enps.form.periodicity.quarterly'),
    },
    {
      value: 'quadrimester',
      label: i18n.t('enps.form.periodicity.quadrimester'),
    },
    {
      value: 'bi_annual',
      label: i18n.t('enps.form.periodicity.bi_annual'),
    },
    {
      value: 'yearly',
      label: i18n.t('enps.form.periodicity.yearly'),
    },
  ]

  const submit = async () => {
    let errorMessage = ''

    setRunning(true)

    const { success, value } = await asyncAction(() => {
      if (textBtn !== stopSurvey) {
        // start a new cycle
        return enpsForm.rpc('start_form', form.data())
      }

      // stop survey
      return enpsForm.rpc('update_form_end_date', {
        end_on: dateToString(moment(), 'backend'),
      })
    })

    setRunning(false)

    if (success) {
      enpsForm.set(value)
      if (enpsForm.isActive) setTextBtn(stopSurvey)
      if (!enpsForm.isActive) setTextBtn(startSurvey)
    } else {
      errorMessage = value
    }

    onSubmitTracking(errorMessage)
  }

  const onSubmitTracking = (errorMessage: string) => {
    if (enpsForm.isFinished) {
      track(trackingEvents.enps.stopSurveySettingsEnps, {
        error_message: errorMessage,
      })
    } else {
      track(trackingEvents.enps.launchSurveyEnps, {
        frequency: enpsForm.periodicity,
        error_message: errorMessage,
      })
    }
  }

  const editEndOn = enpsForm.isDraft ? enpsForm.attributes.end_on : null
  const startOn =
    (enpsForm.isFinished && diffDays < 0) || !start_on
      ? dateToString(moment(), 'backend')
      : start_on

  const form = React.useRef(
    new Form(
      {
        start_on: startOn,
        schedule: enpsForm.periodicity,
        end_on: enpsForm.isFinished ? null : editEndOn,
      },
      {
        start_on: 'date',
        schedule: 'string',
        end_on: 'date',
      }
    )
  ).current

  const onChangeStartDate = (date: Field) => {
    if (date.value === dateToString(today, 'ui')) {
      setTextBtn(startSurvey)
    } else {
      setTextBtn(scheduleSurvey)
    }
  }

  const onChangeFrequency = () => {
    if (moment(form.get('start_on').value).diff(today, 'days') > 0) {
      setTextBtn(scheduleSurvey)
    }
  }

  if (form.get('start_on').value !== startOn && diffDays < 0 && !enpsForm.isFinished) {
    form.setValues({ start_on: startOn })
  }

  return (
    <Section.Form
      illustration="settings"
      title={i18n.t('enps.form.date_frequency.title')}
      description={i18n.t('enps.form.date_frequency.subtitle')}
      submitLabel={i18n.t('common.submit')}
    >
      <Box gap="s16">
        <Box gap="s8">
          <FormRow>
            <DayPicker
              readonly={readOnly}
              mandatory
              field={form.get('start_on')}
              label={i18n.t('enps.form.date_frequency.start_on')}
              minDate={today}
              onChange={(date) => onChangeStartDate(date)}
            />
          </FormRow>
          <FormRow>
            <Select
              readonly={readOnly}
              label={i18n.t('enps.form.date_frequency.frequency')}
              field={form.get('schedule')}
              options={periodicityOptions}
              onChange={() => onChangeFrequency()}
            />
          </FormRow>
          <FormRow>
            <Button type="primary" onClick={submit} disabled={running}>
              {textBtn}
            </Button>
          </FormRow>
        </Box>
      </Box>
    </Section.Form>
  )
}

export default observer(DateFrequency)
